import { type ReactNode, useMemo } from 'react';
import { HStack, Link, Text, VStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { formatPrice } from '../../../../lib/formatPrice';
import type { OrderProductProps } from '../../lib/types';
import styles from './OrderProductInfo.module.css';

interface OrderProductInfoProps
    extends Pick<
            OrderProductProps,
            'url' | 'isExpressCheckout' | 'name' | 'caption' | 'finalPrice' | 'quantity' | 'price'
        >,
        TestProps {
    variantsInfo?: ReactNode;
}

export const OrderProductInfo = (props: OrderProductInfoProps) => {
    const {
        'data-testid': dataTestId = 'OrderProductInfo',
        url,
        isExpressCheckout,
        name,
        caption,
        finalPrice,
        quantity,
        price,
        variantsInfo,
    } = props;

    const quantityString = useMemo(() => {
        if (quantity < 2) {
            return '';
        }

        return `${quantity}\u00a0×\u00a0`;
    }, [quantity]);

    return (
        <div className={styles.contentContainer} data-testid={dataTestId}>
            <VStack gap="6">
                <div className={styles.title}>
                    <HStack {...(url && isExpressCheckout ? { as: Link, href: url, target: '_blank' } : {})}>
                        <Text className={styles.productName} size="mc" data-testid={`${dataTestId}.Title`}>
                            {name}
                        </Text>
                    </HStack>
                    <Text size="mc" role="supplementary" data-testid={`${dataTestId}.Caption`}>
                        {caption}
                    </Text>
                </div>

                {variantsInfo}
            </VStack>

            <div className={styles.prices}>
                <Text size="mc" data-testid={`${dataTestId}.FinalPrice`}>
                    {quantityString}
                    {formatPrice(finalPrice)}
                </Text>

                {price && price !== finalPrice && (
                    <Text
                        size="s"
                        role="supplementary"
                        className={styles.priceOld}
                        data-testid={`${dataTestId}.PriceOld`}
                    >
                        {formatPrice(price)}
                    </Text>
                )}
            </div>
        </div>
    );
};
